import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateSlot, useGetSlotById, useUpdateSlot } from "../../queries/advertisement";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from '../../components/Forms/ActionButton';
import ActiveStatus from '../../components/Common/ActiveStatus';
import NewDropdown from '../../components/Forms/NewDropdown';

const CreateSlot = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const slotId = state?.slotId || null;

    const { data: slotData, isLoading: isSlotDataLoading } = useGetSlotById(slotId);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
        trigger,
    } = useForm({
        defaultValues: {
            slotName: '',
            slotLocation: '',
            status: 'Free',
        }
    });

    const [active, setActive] = useState(true);

    const { mutateAsync: createSlot, isPending: isCreating } = useCreateSlot();
    const { mutateAsync: updateSlot, isPending: isUpdating } = useUpdateSlot(slotId);

    const watchedSlotLocation = watch("slotLocation");
    const watchedStatus = watch("status");

    const onSubmit = async (data) => {
        try {
            const finalData = {
                active: active,
                name: data.slotName,
                location: data.slotLocation,
                status: data.status,
            };

            console.log('Processed slot data:', finalData);

            if (slotId) {
                finalData.id = slotId;
                await updateSlot(finalData, {
                    onSuccess: (res) => {
                        if (res.error) {
                            console.log(res);
                            toast.error(res.error);
                        } else {
                            toast.success('Slot updated successfully!');
                            setTimeout(() => {
                                navigate('/advertisements/slots-management/list-slots');
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        console.error('Error updating slot:', error);
                        toast.error('Failed to update slot. Please try again.');
                    }
                });
            } else {
                await createSlot(finalData, {
                    onSuccess: (res) => {
                        if (res.error) {
                            console.log(res);
                            toast.error(res.error);
                        } else {
                            toast.success('Slot created successfully!');
                            reset();
                            setTimeout(() => {
                                navigate('/advertisements/slots-management/list-slots');
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        console.error('Error creating slot:', error);
                        toast.error('Failed to create slot. Please try again.');
                    }
                });
            }
        } catch (error) {
            console.error('Error saving slot:', error);
            toast.error(`Error saving slot: ${error.message || 'Unknown error'}`);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            if (slotData && slotData.data) {
                const slot = slotData.data;
                setActive(slot.active !== undefined ? slot.active : true);
                setValue("slotName", slot.name || '');
                setValue("slotLocation", slot.location || '');
                setValue("status", slot.status || 'Free');
            }
        }, 200);
    }, [slotData, setValue]);

    if (isSlotDataLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-black">Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <div className='flex items-center justify-end'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold overflow-visible">
                <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                    
                    <div className="flex gap-6">
                        {/* Main Form Fields */}
                        <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Slot Name */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Slot Name
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Slot Name"
                                            {...register("slotName", {
                                                required: "Slot Name is required",
                                                minLength: { value: 2, message: "Slot name must be at least 2 characters" }
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.slotName ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.slotName && <p className="text-red text-sm">{errors.slotName.message}</p>}
                                </div>

                                {/* Slot Location */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="slotLocation" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Slot Location (Page)
                                    </label>
                                    <NewDropdown
                                        name={"slotLocation"}
                                        dataList={[
                                            { _id: 'challenges-homepage', name: 'Challenges Home Page' },
                                            { _id: 'events', name: 'Events' },
                                            { _id: 'blogs', name: 'Blogs' },
                                            { _id: 'knowledge-board-news', name: 'Knowledge Board News' },
                                            { _id: 'knowledge-board-tips', name: 'Knowledge Board Tips' },
                                            { _id: 'knowledge-board-videos', name: 'Knowledge Board Videos' },
                                            { _id: 'eco-activities', name: 'Eco Activities' },
                                            { _id: 'new-challenges', name: 'New Challenges' },
                                        ]}
                                        value={watchedSlotLocation}
                                        placeholder={'Select Page'}
                                        onChange={(e) => {
                                            setValue("slotLocation", e.value);
                                            if (e.value) {
                                                trigger("slotLocation");
                                            }
                                        }}
                                    />
                                    {errors.slotLocation && <p className="text-red text-sm mt-1">{errors.slotLocation.message}</p>}
                                </div>

                                {/* Status */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Status
                                    </label>
                                    <NewDropdown
                                        name={"status"}
                                        dataList={[
                                            { _id: 'Free', name: 'Free' },
                                            { _id: 'Allocated', name: 'Allocated' }
                                        ]}
                                        value={watchedStatus}
                                        placeholder={'Select Status'}
                                        onChange={(e) => {
                                            setValue("status", e.value);
                                            if (e.value) {
                                                trigger("status");
                                            }
                                        }}
                                    />
                                    {errors.status && <p className="text-red text-sm mt-1">{errors.status.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="mt-10 flex gap-5 items-center justify-between">
                        <ActionButton
                            type="button"
                            variant="reset"
                            onClick={() => {
                                reset();
                                setActive(true);
                            }}
                            text="Reset"
                            disabled={isSubmitting}
                        />
                        <div className="flex items-center gap-3">
                            <ActionButton
                                variant="cancel"
                                text="Cancel"
                                handleSubmit={() => navigate('/advertisements/slots-management/list-slots')}
                            />
                            <ActionButton
                                type="submit"
                                variant="primary"
                                text={slotId ? 'Update Slot' : 'Create Slot'}
                                disabled={isCreating || isUpdating}
                            />
                        </div>
                    </div>
                </section>
            </form>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default CreateSlot;
